import { SARAH_RELATIONSHIPS, RELATIONSHIP_TYPE_OPTIONS, type Relationship, type RelationshipKind, type RelationshipStatus, type RelationshipSummary } from './mockData.js';
import { dateInput, validDate } from '../HorsesReview/reviewState.js';

export const RELATIONSHIP_REVIEW_KEY = 'relationship-review';
export interface RelationshipEditorValues {
  type: RelationshipKind;
  horseId: string;
  specialty: string;
  organization: string;
  contact: string;
  location: string;
  startDate: string;
  notes: string;
}
export interface EndOrReplaceValues { endedDate: string; reason: string; next: 'end' | 'replace' }
export interface ReviewRelationship extends Relationship { revision: number }
export interface RelationshipDraft {
  customerId: string;
  mode: 'add' | 'edit';
  targetId: string | null;
  revision: number;
  baseline: RelationshipEditorValues;
  values: RelationshipEditorValues;
}
export interface RelationshipEndDraft {
  customerId: string;
  revision: number;
  values: EndOrReplaceValues;
}
export interface RelationshipChange {
  id: string;
  customerId: string;
  horseId: string;
  relationshipId: string;
  kind: 'added' | 'updated' | 'ended';
  actor: string;
  recordedAt: string;
  effectiveDate: string;
  before: Relationship | null;
  after: Relationship;
}
export interface RelationshipReviewState {
  customerId: string;
  relationships: ReviewRelationship[];
  drafts: Record<string, RelationshipDraft>;
  endDrafts: Record<string, RelationshipEndDraft>;
  selectedHorseId: string;
  filter: RelationshipStatus;
  history: RelationshipChange[];
  nextId: number;
}
const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
export function relationshipValues(record: Relationship | null, horseId: string): RelationshipEditorValues {
  return record ? { type: record.type, horseId: record.linkedHorseId, specialty: record.specialty, organization: record.organization,
    contact: record.contact, location: record.location, startDate: dateInput(record.startDate), notes: record.notes }
    : { type: 'Barn', horseId, specialty: '', organization: '', contact: '', location: '', startDate: '', notes: '' };
}
export function createRelationshipReviewState(customerId: string): RelationshipReviewState {
  return { customerId, relationships: SARAH_RELATIONSHIPS.map(record => ({ ...clone(record), revision: 1 })), drafts: {}, endDrafts: {},
    selectedHorseId: 'horse_duke', filter: 'current', history: [], nextId: 1 };
}
export function openRelationshipDraft(state: RelationshipReviewState, targetId: string | null, key = targetId ? `edit:${targetId}` : 'new'): RelationshipReviewState {
  if (state.drafts[key]) return state;
  const target = targetId ? state.relationships.find(record => record.id === targetId) : null;
  if (targetId && !target) throw Error('This connection is not available for the current customer.');
  const values = relationshipValues(target ?? null, state.selectedHorseId);
  return { ...state, drafts: { ...state.drafts, [key]: { customerId: state.customerId, mode: target ? 'edit' : 'add',
    targetId, revision: target?.revision ?? 0, baseline: clone(values), values } } };
}
export function relationshipDraftDirty(draft: RelationshipDraft): boolean {
  return draft.mode === 'add' || JSON.stringify(draft.values) !== JSON.stringify(draft.baseline);
}
export function endDraftDirty(draft: RelationshipEndDraft): boolean {
  return Boolean(draft.values.endedDate || draft.values.reason || draft.values.next !== 'end');
}
export function hasRelationshipDrafts(state: RelationshipReviewState): boolean {
  return Object.values(state.drafts).some(relationshipDraftDirty) || Object.values(state.endDrafts).some(endDraftDirty);
}
export function discardRelationshipDraft(state: RelationshipReviewState, key: string): RelationshipReviewState {
  const drafts = { ...state.drafts }; delete drafts[key]; return { ...state, drafts };
}
export function openRelationshipEndDraft(state: RelationshipReviewState, targetId: string): RelationshipReviewState {
  const target = state.relationships.find(record => record.id === targetId);
  if (!target || target.status !== 'current') throw Error('This current connection is no longer available.');
  if (state.endDrafts[targetId]) return state;
  return { ...state, endDrafts: { ...state.endDrafts, [targetId]: { customerId: state.customerId, revision: target.revision,
    values: { endedDate: '', reason: '', next: 'end' } } } };
}
export function discardRelationshipEndDraft(state: RelationshipReviewState, targetId: string): RelationshipReviewState {
  const endDrafts = { ...state.endDrafts }; delete endDrafts[targetId]; return { ...state, endDrafts };
}

export function saveRelationshipDraft(state: RelationshipReviewState, key: string, horseIds: string[], recordedAt = new Date().toISOString()): { state: RelationshipReviewState; key: string; changed: boolean } {
  const draft = state.drafts[key];
  const old = draft?.targetId ? state.relationships.find(record => record.id === draft.targetId) : null;
  if (!draft || draft.customerId !== state.customerId || (draft.mode === 'edit' && (!old || old.revision !== draft.revision))) {
    throw Error('This connection changed. Keep the draft, then discard it and review the current record before saving.');
  }
  const values = Object.fromEntries(Object.entries(draft.values).map(([field, value]) => [field, value.trim()])) as unknown as RelationshipEditorValues;
  if (!values.organization && !values.contact) throw Error('Enter an organization or contact name.');
  if (!horseIds.includes(values.horseId)) throw Error('Choose a horse attached to this customer.');
  if (!RELATIONSHIP_TYPE_OPTIONS.includes(values.type)) throw Error('Choose a relationship type.');
  if (values.startDate && !validDate(values.startDate, false)) throw Error('Check the effective date, or leave it blank if unknown.');
  if (old?.endedDate && values.startDate && dateInput(old.endedDate) && values.startDate > dateInput(old.endedDate)) throw Error('The effective date cannot follow the recorded end date.');
  const changed = (Object.keys(values) as Array<keyof RelationshipEditorValues>).filter(field => values[field] !== draft.baseline[field]);
  if (draft.mode === 'edit' && !changed.length) return { state, key, changed: false };
  const id = old?.id ?? `rel_preview_${state.nextId}`;
  const next: ReviewRelationship = old ? { ...old } : { id, type: values.type, linkedHorseId: values.horseId, specialty: '', organization: '', contact: '', location: '', startDate: '', notes: '', status: 'current', verification: 'Identity not verified', symbolTone: 'default', revision: 0 };
  for (const field of (old ? changed : Object.keys(values)) as Array<keyof RelationshipEditorValues>) {
    if (field === 'horseId') next.linkedHorseId = values.horseId;
    else Object.assign(next, { [field]: values[field] });
  }
  // Editing an asserted identity does not preserve an earlier verification claim.
  if (old && changed.some(field => ['organization', 'contact'].includes(field)) && next.status === 'current') next.verification = 'Identity not verified';
  next.revision++;
  const event: RelationshipChange = { id: `relationship_change_${state.nextId}`, customerId: state.customerId, horseId: next.linkedHorseId,
    relationshipId: id, kind: old ? 'updated' : 'added', actor: 'Jamie Dorsey · Preview', recordedAt,
    effectiveDate: next.startDate, before: old ? clone(old) : null, after: clone(next) };
  const drafts = { ...state.drafts }; delete drafts[key];
  const savedKey = `edit:${id}`, savedValues = relationshipValues(next, next.linkedHorseId);
  drafts[savedKey] = { customerId: state.customerId, mode: 'edit', targetId: id, revision: next.revision, baseline: clone(savedValues), values: savedValues };
  return { key: savedKey, changed: true, state: { ...state, relationships: old ? state.relationships.map(record => record.id === id ? next : record) : [...state.relationships, next],
    selectedHorseId: next.linkedHorseId, filter: next.status, drafts, history: [...state.history, event], nextId: state.nextId + 1 } };
}

export function endRelationship(state: RelationshipReviewState, targetId: string, recordedAt = new Date().toISOString()): { state: RelationshipReviewState; replacementKey: string | null } {
  const draft = state.endDrafts[targetId], old = state.relationships.find(record => record.id === targetId);
  if (!draft || draft.customerId !== state.customerId || !old || old.status !== 'current' || old.revision !== draft.revision) throw Error('This connection changed. Discard the earlier end draft and review the current record.');
  const { endedDate, reason, next: choice } = draft.values;
  if (!validDate(endedDate, false)) throw Error('Select a valid date this connection ended.');
  const start = dateInput(old.startDate);
  if (start && endedDate < start) throw Error('End date cannot precede the effective date.');
  const next: ReviewRelationship = { ...old, status: 'former', verification: 'Former', endedDate, endReason: reason.trim(), revision: old.revision + 1 };
  const event: RelationshipChange = { id: `relationship_change_${state.nextId}`, customerId: state.customerId, horseId: old.linkedHorseId,
    relationshipId: old.id, kind: 'ended', actor: 'Jamie Dorsey · Preview', recordedAt, effectiveDate: endedDate, before: clone(old), after: clone(next) };
  const endDrafts = { ...state.endDrafts }; delete endDrafts[targetId];
  let result: RelationshipReviewState = { ...state, endDrafts, relationships: state.relationships.map(record => record.id === targetId ? next : record),
    history: [...state.history, event], filter: 'former', nextId: state.nextId + 1 };
  const replacementKey = choice === 'replace' ? `replacement:${targetId}:${state.nextId}` : null;
  if (replacementKey) {
    result = openRelationshipDraft(result, null, replacementKey);
    const replacement = result.drafts[replacementKey];
    const values = { ...replacement.values, type: old.type, horseId: old.linkedHorseId, specialty: old.specialty, startDate: endedDate };
    result.drafts[replacementKey] = { ...replacement, values, baseline: clone(values) };
  }
  return { state: result, replacementKey };
}

export function summariseRelationships(records: Relationship[], horseId: string, horseName: string): RelationshipSummary {
  const scoped = records.filter(record => record.linkedHorseId === horseId);
  const current = scoped.filter(record => record.status === 'current'), former = scoped.length - current.length;
  const barn = current.find(record => record.type === 'Barn');
  const vet = current.find(record => record.type === 'Veterinarian' && /\bprimary vet(?:erinarian)?\b/i.test(record.specialty));
  const farrier = current.find(record => record.type === 'Shoer / Farrier' && /\bprimary (?:farrier|shoer)\b/i.test(record.specialty));
  return { horseName, currentCount: current.length, formerSummary: `${former} former connection${former === 1 ? '' : 's'} retained in history`,
    currentBarn: { name: barn?.organization || barn?.contact || 'Not recorded', location: barn?.location || 'Not recorded', since: barn?.startDate ? `since ${barn.startDate}` : 'Date not recorded' },
    primaryVet: { name: vet?.contact || vet?.organization || 'Not recorded', practice: vet?.organization || 'Primary role not recorded' },
    primaryFarrier: { name: farrier?.contact || farrier?.organization || 'Not recorded', cadence: farrier?.specialty || 'Primary role not recorded' } };
}
